import { alertService, createAsyncBuffer, createStorePersist, RequestStatus, sleep } from "common-frontend-components";
import { useEffect } from "react";
import { galleryCategoryAssociationsStore } from "./galleryCategoryAssociationsStore";
import { getStorageCall } from "@yehonadav/safestorage";
import { categoriesStore } from "../useCategoriesStore";
import { callGetGalleriesAsync, setGalleryDates, sortGalleries } from "../../apis/galleries";
const getDefaultNewGallery = () => ({
    name: '',
    description: '',
    categories: [],
    files: [],
    index: undefined,
});
const persistOptions = {
    name: `ramibarilan/galleries/store`,
    whitelist: ["galleries"],
    getStorage: getStorageCall,
};
const { useStore, fetchStore, set, get } = createStorePersist({
    persistOptions,
    persistAfterClearingStorage: false,
    getDefaultValues: () => ({
        galleries: {},
        galleryList: [],
        newGallery: getDefaultNewGallery(),
        dataFiltered: [],
        pageIndex: 0,
        rowsPerPage: 25,
        pageItems: [],
        requestStatus: RequestStatus.pending,
        tab: 0,
        tabPanelsDelayedValue: 0,
    })
});
const getGalleries = () => get().galleryList;
const setGalleries = (galleryList) => {
    const galleries = {};
    galleryList.forEach(gallery => { galleries[gallery.id] = gallery; });
    set({ galleries, galleryList });
};
// set galleries from storage
setGalleries(Object.values(get().galleries).map(i => {
    setGalleryDates(i);
    return i;
}));
const getDataFiltered = () => get().dataFiltered;
const getPageIndex = () => get().pageIndex;
const getRowsPerPage = () => get().rowsPerPage;
const getPageItems = () => get().pageItems;
const setDataFiltered = (dataFiltered) => set({ dataFiltered });
const setPageIndex = (pageIndex) => set({ pageIndex });
const setRowsPerPage = (rowsPerPage) => set({ rowsPerPage });
const setPageItems = (pageItems) => set({ pageItems });
const getTab = () => get().tab;
const getTabPanelsDelayedValue = () => get().tabPanelsDelayedValue;
const tabAsyncBuffer = createAsyncBuffer();
const setTab = (tab) => {
    set({ tab });
    tabAsyncBuffer(async () => {
        await sleep(500);
        set({ tabPanelsDelayedValue: tab });
    });
};
const handleTabChange = (_event, newValue) => {
    setTab(newValue);
};
const useTab = () => useStore(fetchStore.tab);
const useTabPanelsDelayedValue = () => useStore(fetchStore.tabPanelsDelayedValue);
const handleChangePage = (_event, newPage) => {
    setPageIndex(newPage);
};
const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageIndex(0);
};
const _getPageItems = () => {
    const rowsPerPage = getRowsPerPage();
    const skip = getPageIndex() * rowsPerPage;
    return getDataFiltered().slice(skip, skip + rowsPerPage);
};
const useGalleries = () => useStore(fetchStore.galleryList);
const useDataFiltered = () => useStore(fetchStore.dataFiltered);
const usePageItems = () => useStore(fetchStore.pageItems);
const usePageIndex = () => useStore(fetchStore.pageIndex);
const useRowsPerPage = () => useStore(fetchStore.rowsPerPage);
const useFilterData = () => {
    const categoryFilters = categoriesStore.useCategoryFilters();
    const data = useGalleries();
    useEffect(() => {
        let filteredData = data;
        if (categoryFilters.length > 0) {
            filteredData = [];
            categoryFilters.forEach(category => {
                const galleries = galleryCategoryAssociationsStore.getCategoryGalleries(category.id);
                galleries.forEach(gallery => {
                    if (!filteredData.includes(gallery))
                        filteredData.push(gallery);
                });
            });
        }
        setDataFiltered(sortGalleries(filteredData));
    }, [
        data,
        categoryFilters,
    ]);
};
const useGetPageItems = () => {
    const dataFiltered = useDataFiltered();
    const pageIndex = usePageIndex();
    const rowsPerPage = useRowsPerPage();
    useEffect(() => {
        setPageItems(_getPageItems());
    }, [dataFiltered, pageIndex, rowsPerPage]);
};
export const galleriesStore = {
    clearNewGallery: () => set({ newGallery: getDefaultNewGallery() }),
    getNewGallery: () => get().newGallery,
    setNewGallery: (newGallery) => set({ newGallery }),
    setNewGalleryPreviewImg: (preview) => set(s => ({ newGallery: { ...s.newGallery, preview } })),
    setNewGalleryFiles: (files) => set(s => ({ newGallery: { ...s.newGallery, files } })),
    setNewGalleryIndex: (index) => set(s => ({ newGallery: { ...s.newGallery, index } })),
    onNewGalleryCategoryChange: (_event, categories) => set(s => ({ newGallery: { ...s.newGallery, categories } })),
    useNewGalleryCategories: () => useStore(s => s.newGallery.categories),
    useNewGalleryName: () => useStore(s => s.newGallery.name),
    useNewGalleryPreviewImg: () => useStore(s => s.newGallery.preview),
    useNewGalleryIndex: () => useStore(s => s.newGallery.index),
    handleNewGalleryNameChange: (e) => set(s => ({ newGallery: { ...s.newGallery, name: e.target.value } })),
    useNewGalleryDescription: () => useStore(s => s.newGallery.description),
    handleNewGalleryDescriptionChange: (e) => set(s => ({ newGallery: { ...s.newGallery, description: e.target.value } })),
    useNewGalleryFiles: () => useStore(s => s.newGallery.files),
    getGalleryMap: () => get().galleries,
    getGalleries,
    setGalleries,
    useGalleries,
    getDataFiltered,
    getPageIndex,
    getRowsPerPage,
    getPageItems,
    setDataFiltered,
    setPageIndex,
    setRowsPerPage,
    setPageItems,
    handleChangePage,
    handleChangeRowsPerPage,
    useDataFiltered,
    usePageItems,
    usePageIndex,
    useRowsPerPage,
    useFilterData,
    useGetPageItems,
    getTab,
    setTab,
    handleTabChange,
    useTab,
    getTabPanelsDelayedValue,
    useTabPanelsDelayedValue,
    getRequestStatus: () => get().requestStatus,
    // tslint:disable-next-line:no-shadowed-variable
    setRequestStatus: (requestStatus) => { console.log({ requestStatus }); const s = get(); s.requestStatus = requestStatus; set({ requestStatus }); },
    useRequestStatus: () => useStore(fetchStore.requestStatus),
    useLoad: () => {
        const status = galleriesStore.useRequestStatus();
        const data = galleriesStore.useGalleries();
        useEffect(() => {
            if (get().requestStatus !== RequestStatus.pending)
                return;
            galleriesStore.setRequestStatus(RequestStatus.loading);
            callGetGalleriesAsync()
                .then((items) => {
                galleriesStore.setGalleries(items);
                galleriesStore.setRequestStatus(RequestStatus.loaded);
            })
                .catch((e) => {
                alertService.error(e);
                galleriesStore.setRequestStatus(RequestStatus.failed);
            });
        }, [status]);
        return { status, data };
    }
};
//# sourceMappingURL=galleriesStore.js.map